<script setup>
import { ref } from 'vue';
import { modal } from '@shop/frontend/web/js/components/Modal';
import { translator } from '@shop/frontend/web/js/services/TranslatorService';
import { dateHelper } from '@shop/frontend/web/js/helpers/dateHelper';
import ITeams from '../ITeams/ITeams.vue';
import ITicket from '../ITicket/ITicket.vue';
import IRefund from '../IRefund/IRefund.vue';
import IPackageEvent from '../IPackageEvent/IPackageEvent.vue';
import IPackageEventName from '../IPackageEventName/IPackageEventName.vue';
import ILink from '../ILink/ILink.vue';
import ExtraInfo from '../ExtraInfo.vue';
import useCartStore from '../../store/modules/cart.js';

const cartStore = useCartStore();
const props = defineProps({
  itemCase: {
    type: Object,
    required: true,
  },
  trashButton: {
    type: Boolean,
    default: true,
  },
  isHistory: {
    type: Boolean,
    default: false,
  },
});

const emits = defineEmits(['removeEvent']);

function getFormattedDate(date) {
  if (!date) return '';
  return date.replace(
    /^(\d{4})-(\d{2})-(\d{2}) (\d{2}:\d{2}):\d{2}$/,
    '$3.$2.$1 $4',
  );
}

const eventFormattedDate = ref(
  getFormattedDate(props.itemCase.startTimestampStr),
);

function removeTicket(ticket, eventId, type) {
  const adultTicketsCount = props.itemCase.tickets.reduce((
    ticketsCount,
    currentTicket,
  ) => (currentTicket.loyaltyCategoryId === null ? ticketsCount + 1 : ticketsCount), 0);

  const hasChildTicket = props.itemCase.tickets.some(
    (currentTicket) => currentTicket.loyaltyCategoryId === 1,
  );

  if (hasChildTicket && adultTicketsCount <= 1) {
    modal.error(translator.t('order', 'Need at least one adult ticket!'));
    return;
  }

  if (type === 'service_simple') {
    cartStore.removeServiceTicket(ticket, eventId);
  } else {
    cartStore.removeEventTicket(ticket, eventId);
  }
}

function removeEvent(eventId, type, countTickets) {
  if (type === 'service_simple') {
    cartStore.removeService(eventId, props.itemCase.orderId, countTickets);
  } else {
    cartStore.removeEvent(eventId, props.itemCase.orderId);
  }
}

function changeLoyalty(ticket, eventId, categoryId, priceId) {
  cartStore.changeLoyalty(ticket, eventId, props.itemCase.orderId, categoryId, priceId);
}

const isPackageEventsShow = ref(false);
</script>

<template>
  <div class="i-order-event-item">
    <div class="i-order-event-item__top-wrapper">
      <a class="i-order-event-item__team-link" :href="itemCase.pageLink">
        <div class="i-order-event-item__top-left">
          <template v-if="itemCase.type == 'vagrant'">
            <div class="i-order-event-item__name">
              <IPackageEventName
                v-if="itemCase.eventType === 'Пакет' && itemCase.participants?.team"
                :package-event="itemCase"
              />
              <ITeams
                v-else-if="itemCase.participants?.team"
                :first-team="itemCase.participants.team.homeTeam"
                :second-team="itemCase.participants.team.guestTeam"
                :show-city="true"
                :name="itemCase.name"
                :is-home-logo-with-opponent="itemCase.isHomeLogoWithOpponent"
              />
              <div v-else class="name">
                <div class="name__img">
                  <img
                    :src="itemCase.participants?.singular.imageUrl"
                    alt="eventImage"
                  />
                </div>
                <div class="name__title">
                  {{ itemCase.participants?.singular.name }}
                </div>
              </div>
            </div>
            <div v-if="itemCase.eventType !== 'Пакет'" class="i-order-event-item__details">
              <span class="time">
                <svg>
                  <use xlink:href="#calendar"></use>
                </svg>
                <span class="text-capitalize">{{ eventFormattedDate }}</span>
              </span>
              <span class="place">
                <svg>
                  <use xlink:href="#location"></use>
                </svg>
                {{ itemCase.unitName }}
              </span>
            </div>
          </template>
          <template v-else>
            <div
              class="i-order-event-item__name"
              :class="{'i-order-event-item__name_service' : itemCase.type === 'service_simple'}"
            >
              <div class="name">
                <div class="name__title">{{ itemCase.name }}</div>
              </div>
            </div>
          </template>
        </div>
      </a>
      <button
        v-if="itemCase.eventType === 'Пакет'"
        class="i-order-event-item__show-packages-btn"
        :class="{ 'i-order-event-item__show-packages-btn_active' : isPackageEventsShow }"
        @click="isPackageEventsShow = !isPackageEventsShow"
      >
        <svg height="24" width="24">
          <use xlink:href="#chevron-down" />
        </svg>
      </button>
    </div>
    <div class="i-order-event-item__top-right">
      <ILink
        v-if="trashButton"
        :icon-name="'trash'"
        class="desktop trash-btn"
        @click="
          removeEvent(
            itemCase.id,
            type ?? itemCase.type,
            ticketsCount ?? itemCase.tickets.length
          )
        "
      >
        Удалить
      </ILink>
      <div class="i-order-event-item__summary-price">
        {{ translator.t('main', 'Price:') }} <span>{{ itemCase.summaryPrice }} ₽</span>
      </div>
      <ILink
        v-if="trashButton"
        :icon-name="'close'"
        class="mobile close-btn"
        @click="removeEvent(itemCase.id)"
      />
    </div>
    <div
      v-if="itemCase?.packageEvents?.length"
      v-show="isPackageEventsShow"
      class="i-order-event-item__package-events"
    >
      <IPackageEvent
        v-for="packageEvent in itemCase.packageEvents"
        :key="packageEvent.startTimestamp" :package-event="packageEvent"
      />
    </div>
    <div class="i-order-event-item__tickets">
      <slot>
        <div v-for="ticket in itemCase.tickets" :key="ticket.place" class="tickets-item">
          <i-ticket
            :ticket="ticket"
            :is-history="isHistory"
            :inactive="false"
            :short="false"
            :allowed-loyalty="itemCase.allowedLoyaltyCategories"
            @remove-ticket="
              curTicket => removeTicket(curTicket, itemCase.id, itemCase.type)
            "
            @change-loyalty="
              ({ ticket, categoryId, priceId }) =>
                changeLoyalty(ticket, itemCase.id, categoryId, priceId)
            "
          >
            <ExtraInfo
              v-if="ticket.isEnableExtraInfo"
              :key="ticket.uid"
              :ticket="ticket"
              :is-history="isHistory"
            />
          </i-ticket>
        </div>
      </slot>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/web/src/styles/utils/variables.scss";
@import "./i-order-event-item.scss";

.fade-enter-active,
.fade-leave-active {
  transition: height 0.5s ease-in-out;
}

.fade-enter-to,
.fade-leave-from {
  overflow: hidden;
  height: 100%;
}

.fade-enter-from,
.fade-leave-to {
  overflow: hidden;
  height: 0;
}
</style>
